import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Portal-rendered popup menu so the dropdown isn't clipped by table overflow.
// Single source of truth for admin per-user actions across `/admin/users` and
// `OrgDetailModal` (when listing members in the future).
export type AdminUserRow = {
  uid: string;
  email?: string;
  defaultOrgId?: string;
};

const MENU_WIDTH = 240;

export function UserActionsMenu({
  user,
  busy,
  onPromote,
  onTransfer,
  onDelete,
}: {
  user: AdminUserRow;
  busy?: boolean;
  onPromote: () => void;
  onTransfer: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 8,
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onScroll() {
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-40 hover:bg-black/5"
        style={{ color: "#0f0f0f", borderColor: "rgba(0,0,0,0.14)" }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Actions ▾
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="rounded-2xl py-1"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: MENU_WIDTH,
              zIndex: 1000,
              backgroundColor: "#ffffff",
              border: "1px solid rgba(0,0,0,0.10)",
              boxShadow: "0 12px 32px -12px rgba(0,0,0,0.18)",
            }}
          >
            <MenuItem
              disabled={Boolean(user.defaultOrgId)}
              title={
                user.defaultOrgId
                  ? "Already belongs to an organization"
                  : "Create a new org and assign this user as owner"
              }
              onClick={() => {
                setOpen(false);
                onPromote();
              }}
            >
              Promote to org owner
            </MenuItem>
            <MenuItem
              disabled={!user.defaultOrgId}
              title={
                user.defaultOrgId
                  ? "Hand ownership of their org to another member"
                  : "User has no org"
              }
              onClick={() => {
                setOpen(false);
                onTransfer();
              }}
            >
              Transfer ownership
            </MenuItem>
            <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }} />
            <MenuItem
              danger
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              Delete user
            </MenuItem>
          </div>,
          document.body,
        )}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  title?: string;
}) {
  return (
    <button
      role="menuitem"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`w-full text-left px-4 py-2 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${danger ? "hover:bg-red-50" : "hover:bg-black/5"}`}
      style={{ color: danger ? "#C61418" : "#0f0f0f" }}
    >
      {children}
    </button>
  );
}
